import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Stack, TextField } from "@mui/material";

import { format } from "date-fns";

import { useNavigate, useSearchParams } from "react-router-dom";

import SquareCheck from "@assets/icons/list-builder/square-check.svg?url";
import Square from "@assets/icons/list-builder/square.svg?url";
import { ReactComponent as MagnifyingGlass } from "@assets/icons/list-builder/magnifying-glass.svg";

import { ReactComponent as Plus } from "@assets/icons/plus.svg";

import { ReactComponent as ThreeDots } from "@assets/icons/three-dots.svg";

import { ReactComponent as Trash } from "@assets/icons/trash.svg";

import { ReactComponent as Download } from "@assets/icons/download.svg";

import { LoadingOverlay, Modal, PermissionCheck } from "@/components";
import { useSearch } from "@/utils";

import { PermissionRoles } from "@/enum";

import { InputField } from "@/components/InputField/InputField";

import { Toggle } from "@/components/Toggle/Toggle";

import { useStickyParams } from "@/utils/useStickyParams";

import { Button } from "@/components/Button/Button";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { Table, TableColumn } from "@/components/Table/Table";
import {
	profileTrackerSelector,
	setProfileTrackerSorting,
} from "@/pages/Private/redux/profileTracker/profileTracker.slice";

import { SortParams } from "@/types/types";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { classNames } from "@/utils/classNames";
import { ContextMenu, MenuItem } from "@/components/ContextMenu/ContextMenu";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import {
	useDeleteTrackerMutation,
	useGetProfileTrackersQuery,
	useUpdateTrackerMutation,
	useDownloadTrackersMutation,
} from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { ListRunningStatus, ListStatus } from "@/enum/list.enum";

import { useBulkFunctions } from "@/utils/userBulkFunctions";

import { Tracker, UpdateTracker } from "../schema/profileTracker";
import { getOrderStatus } from "../utils/colum-utils";

export const ProfileTrackerTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`profileTracker.${key}`), [t]);

	const [searchParams] = useSearchParams();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

	const [selectedProfileTracker, setSelectedProfileTracker] = useState<Tracker | null>(null);

	const { searchValue, setSearchValue, query } = useSearch();

	const { selectedItems, selectAll, handleSelectAll, handleSelectItem, toggleSelectAll } =
		useBulkFunctions();

	const handleSorting = (sortParams: SortParams) => {
		dispatch(setProfileTrackerSorting(sortParams));
	};

	const {
		sortingProfileTracker,
		profileTracker: data,
		profileTrackerMeta,
	} = useAppSelector(profileTrackerSelector);

	const [showOnlyRunning, setShowOnlyRunning] = useState<boolean | undefined>(undefined);

	const [deleteTracker, { isLoading: deleteIsLoading }] = useDeleteTrackerMutation();
	const [updateTracker] = useUpdateTrackerMutation();
	const [bulkDeleteTrackers] = useDeleteTrackerMutation();

	const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

	const [downloadTrackers, { isLoading: downloadIsLoading }] = useDownloadTrackersMutation();

	const [localLoading, setLocalLoading] = useState(false);
	const [page, setPage] = useState(1);

	useEffect(() => {
		if (searchParams.has("profileTracker")) {
			const cProfileTrackerName = searchParams.get("profileTracker");

			if (cProfileTrackerName) {
				setSearchValue(cProfileTrackerName);
			}
		}
	}, [searchParams, setSearchValue]);

	useStickyParams("profileTracker", setSearchValue, searchValue);

	const { isLoading, isFetching } = useGetProfileTrackersQuery(
		{
			page,
			limit: 15,
			searchValue: query,
			active: showOnlyRunning,
			sorting: sortingProfileTracker,
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const [prevParams, setPrevParams] = useState({
		query,
		showOnlyRunning,
	});

	useEffect(() => {
		if (localLoading) {
			setLocalLoading(false);
		}

		if (data) {
			if (showOnlyRunning !== prevParams.showOnlyRunning || query !== prevParams.query) {
				setPage(1);
			}
		}

		setPrevParams({ query, showOnlyRunning });
	}, [data, query]);

	const handleToggle = useCallback(async (id: number, status: ListStatus) => {
		const updater: Partial<UpdateTracker> = {
			id: id,
		};

		if (status === ListStatus.ACTIVE) {
			updater.activeStatus = ListStatus.PAUSED;
		} else if (status === ListStatus.PAUSED) {
			updater.activeStatus = ListStatus.ACTIVE;
		}

		await updateTracker({ ...updater }).unwrap();
	}, []);

	const tableColumns: TableColumn[] = useMemo(() => {
		const columns: TableColumn[] = [
			{
				title: "",
				uid: "select",
				width: "52px",
				svgIcon: selectAll ? SquareCheck : Square,
				show: true,
				onHeaderClick: () => {
					toggleSelectAll();
				},
				render: (row: Tracker, index?: number) => (
					<div className="group flex items-center">
						<div
							className={classNames(
								"w-[16px] text-center font-medium text-table-header",
								selectedItems?.find((lf) => lf === row.id) ? "hidden" : "group-hover:hidden"
							)}
						>
							{typeof index !== "undefined" ? index + 1 : ""}
						</div>
						<Checkbox
							className={classNames(
								selectedItems?.find((lf) => lf === row.id) ? "block" : "hidden group-hover:block"
							)}
							isChecked={!!selectedItems?.find((lf) => lf === row.id)}
							name={"selected"}
							onChange={(e) => {
								e.preventDefault();
								e.stopPropagation();

								handleSelectItem(row.id);
							}}
						/>
					</div>
				),
			},
			{
				title: ts("status.title"),
				width: "100px",
				orderTitle: "status",
				orderStatus: getOrderStatus(sortingProfileTracker, "status"),
				show: true,
				render: (row: Tracker) => (
					<div>
						<Toggle
							isChecked={row?.activeStatus === ListStatus.ACTIVE}
							onChange={(e) => {
								e.stopPropagation();
								e.preventDefault();

								if (row.id && row.activeStatus !== ListStatus.ERROR) {
									handleToggle(row.id, row.activeStatus);
								}
							}}
						/>
					</div>
				),
			},
			{
				title: ts("name"),
				maxWidth: "330px",
				orderTitle: "name",
				orderStatus: getOrderStatus(sortingProfileTracker, "name"),
				uid: "name",
				render: (row: Tracker) => (
					<div className="flex">
						<Link href={`/app/profile-tracker/detail/${row?.id}`}>{row?.name}</Link>
					</div>
				),
			},
			{
				title: "Organization count",
				maxWidth: "330px",
				uid: "organizationCount",
				render: (row: Tracker) => <div className="flex">{row.organizationCount}</div>,
			},
			{
				title: "Person count",
				maxWidth: "330px",
				uid: "organizationCount",
				render: (row: Tracker) => <div className="flex">{row.people}</div>,
			},
			{
				title: "last signal",
				maxWidth: "330px",
				orderTitle: "lastSignal",
				orderStatus: getOrderStatus(sortingProfileTracker, "lastSignal"),
				uid: "lastSignal",
				render: (row: Tracker) => (
					<div className="text-sm">
						{row?.lastSignal ? format(new Date(row?.lastSignal), "dd.MM.yyyy HH:mm") : ""}
					</div>
				),
			},

			{
				title: ts("lastModifiedAt"),
				width: "140px",
				orderTitle: "updatedAt",
				orderStatus: getOrderStatus(sortingProfileTracker, "updatedAt"),
				uid: "lastModifiedAt",
				render: (row: Tracker) => (
					<div className="flex">
						<div className="text-sm">{format(new Date(row?.updatedAt), "dd.MM.yyyy HH:mm")}</div>
					</div>
				),
			},

			{
				title: "",
				minWidth: 56,
				render: (row: Tracker) => {
					const options: MenuItem[] = [
						{
							title: ts("editProfileTracker"),
							onClick: () => {
								navigate(`/app/profile-tracker/edit/${row.id}`);
							},
						},
						{
							title: ts("deleteProfileTracker"),
							permissions: [PermissionRoles.TRACKER],
							onClick: () => {
								handleDeleteModal(row);
							},
						},
					];

					return (
						<Stack direction="row" justifyContent="flex-end" spacing={2}>
							<ContextMenu data={[options]} position="bottom-right" width="150">
								<SvgIcon
									className="w-[20px] h-[20px] text-gray-700 cursor-pointer"
									svgIcon={ThreeDots}
								/>
							</ContextMenu>
						</Stack>
					);
				},
			},
		];

		return columns;
	}, [selectAll, ts, t, toggleSelectAll]);

	const handleSelectStatus = (value: AutoCompleteItem) => {
		if (value.id === ListRunningStatus.ALL) {
			setShowOnlyRunning(undefined);
		} else if (value.id === ListRunningStatus.ACTIVE) {
			setShowOnlyRunning(true);
		} else if (value.id === ListRunningStatus.PAUSED) {
			setShowOnlyRunning(false);
		}
	};

	function getActiveStatus(): string | number | null {
		if (showOnlyRunning) {
			return ListRunningStatus.ACTIVE;
		}

		if (showOnlyRunning === false) {
			return ListRunningStatus.PAUSED;
		}

		return ListRunningStatus.ALL;
	}

	function getActivityStatusText(): string {
		if (showOnlyRunning) {
			return ts("activity.running");
		}

		if (showOnlyRunning === false) {
			return ts("activity.stopped");
		}

		return ts("activity.all");
	}

	const handleDeleteModal = (row: Tracker) => {
		setShowDeleteModal(true);
		setSelectedProfileTracker(row);
	};

	const handleDeleteTracker = async () => {
		try {
			await deleteTracker(selectedProfileTracker?.id ?? 0).unwrap();
			setShowDeleteModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	const handleFetchMore = () => {
		if (selectAll) {
			toggleSelectAll();
		}

		setPage(page + 1);
	};

	const handleBulkDelete = async () => {
		try {
			await Promise.all(selectedItems.map((id) => bulkDeleteTrackers(id).unwrap()));
			setShowBulkDeleteModal(false);
			handleSelectAll([]);
		} catch (err) {
			console.error(err);
		}
	};

	const handleDownloadSelected = async () => {
		const ids = selectedItems.join(",");

		try {
			const blob = await downloadTrackers(ids).unwrap();

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");

			link.href = url;
			link.setAttribute("download", "trackers.xlsx");

			document.body.appendChild(link);
			link.click();

			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(url);

			handleSelectAll([]);
		} catch (error) {
			console.error("Download failed:", error);
			// Handle error appropriately
		}
	};

	return isLoading ? (
		<LoadingOverlay />
	) : (
		<div className="w-full">
			<div className="flex items-end align-baseline justify-between  mb-8">
				<div className="flex items-end align-baseline justify-between w-1/2">
					<div className="flex-grow mr-4 ">
						<InputField
							handleChange={(e: ChangeEvent<HTMLInputElement>) => {
								setLocalLoading(true);
								setSearchValue(e.target.value);
							}}
							iconInside={<SvgIcon className="text-gray-500" svgIcon={MagnifyingGlass} />}
							name={"search"}
							placeholder={ts("search")}
							value={searchValue}
						/>
					</div>
				</div>

				<div className="flex items-center justify-end gap-2 mb-3">
					<Dropdown
						data={[
							{
								title: ts("activity.all"),
								id: ListRunningStatus.ALL,
							},
							{
								title: ts("activity.running"),
								id: ListRunningStatus.ACTIVE,
							},
							{
								title: ts("activity.stopped"),
								id: ListRunningStatus.PAUSED,
							},
						]}
						defaultValue={{
							title: ts("activity.all"),
							id: ListRunningStatus.ALL,
						}}
						floating={true}
						handleSelect={(value: AutoCompleteItem) => {
							handleSelectStatus(value);
						}}
						title={ts("status.title")}
						value={{
							title: getActivityStatusText(),
							id: getActiveStatus(),
						}}
					/>
					<PermissionCheck requiredPermissions={[PermissionRoles.TRACKER]}>
						<Button
							color={ButtonColor.ACTION}
							image={<SvgIcon className="w-[16px] h-[16px] mr-2" svgIcon={Plus} />}
							size={ButtonSize.ML}
							testId="new-user-button"
							title={`Add Custom Signal Tracker`}
							onClick={() => navigate("/app/profile-tracker/create")}
						/>
					</PermissionCheck>
				</div>
			</div>

			{isLoading ? <LoadingOverlay /> : ""}
			<div className="relative w-full">
				<div className="pr-4 pb-[20px]">
					<Table
						columns={tableColumns}
						data={data || []}
						handleExport={() => {
							// handleDownloadSelected(); //for export
						}}
						handleFetchMore={() => {
							if (
								profileTrackerMeta?.itemCount &&
								data.length < profileTrackerMeta?.itemCount &&
								!isFetching &&
								page < profileTrackerMeta.pageCount
							) {
								handleFetchMore();
							}
						}}
						handleSorting={(sortParams: SortParams) => {
							setPage(1);
							handleSorting(sortParams);
						}}
						hideColumnsCount={true}
						hidePagination={true}
						isFetching={isFetching}
						isLoading={isLoading || localLoading}
						overallCount={profileTrackerMeta.itemCount}
						page={page}
						totalPages={profileTrackerMeta.pageCount}
					/>
				</div>
			</div>
			<div
				className={classNames(
					"items-end align-baseline justify-start fixed bottom-12 z-50 bg-white w-full py-4",
					selectedItems.length > 0 ? "flex" : "hidden"
				)}
			>
				<div className="w-[fit-content] mr-2">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<SvgIcon className="w-[16px] h-[16px] mr-2" svgIcon={Download} />}
						isLoading={downloadIsLoading}
						size={ButtonSize.ML}
						title={`${t("basics.download")} ${selectedItems.length} Custom Signal Tracker`}
						onClick={handleDownloadSelected}
					/>
				</div>
				<div className="w-[fit-content] mr-2">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<SvgIcon className="w-[16px] h-[16px] mr-2" svgIcon={Trash} />}
						isLoading={deleteIsLoading}
						size={ButtonSize.ML}
						title={`${t("basics.delete")} ${selectedItems.length} Custom Signal Tracker`}
						onClick={() => setShowBulkDeleteModal(true)}
					/>
				</div>
			</div>

			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeleteTracker}
					isLoading={deleteIsLoading}
					isOpened={showDeleteModal}
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={ts("delete.title")}
				>
					<div className="text-sm text-gray-700 mb-4">{ts("delete.confirmation")}</div>
					<TextField
						fullWidth
						defaultValue={selectedProfileTracker?.name}
						inputProps={{ readOnly: true }}
						label={ts("name")}
					/>
				</Modal>
			)}

			{showBulkDeleteModal && (
				<Modal
					handleClose={() => setShowBulkDeleteModal(false)}
					handleSave={handleBulkDelete}
					isLoading={deleteIsLoading}
					isOpened={showBulkDeleteModal}
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={ts("delete.title")}
				>
					<div className="text-sm text-gray-700 mb-4">
						{t("basics.deleteMultipleConfirmation", { count: selectedItems.length })}
					</div>
				</Modal>
			)}
		</div>
	);
};
